import axios from 'axios';
import { API_URL } from '../config';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Add a request interceptor to add the JWT token to requests
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * Document service for handling document-related API calls
 */
export const documentService = {
  /**
   * Get all documents for the current user
   * @returns {Promise<Object>} - Response with documents array
   */
  async getDocuments() {
    try {
      const response = await api.get('/documents');
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },

  /**
   * Upload a new document
   * @param {FormData} formData - Form data with file and metadata
   * @returns {Promise<Object>} - Response with uploaded document
   */
  async uploadDocument(formData) {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },
  
  /**
   * Upload multiple documents at once
   * @param {FormData} formData - Form data with files array and metadata
   * @returns {Promise<Object>} - Response with uploaded documents
   */
  async uploadDocumentsBatch(formData) {
    try {
      // Log what's in the FormData for debugging
      console.log('FormData contents:');
      for (let pair of formData.entries()) {
        if (pair[0] === 'files') {
          console.log(pair[0], ':', pair[1].name, '(', pair[1].size, 'bytes )');
        } else {
          console.log(pair[0], ':', pair[1]);
        }
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/documents/batch-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        // Add timeout and retry options
        timeout: 30000, // 30 seconds timeout
      });
      return response.data;
    } catch (error) {
      console.error('Error batch uploading documents:', error);
      // Provide more detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
      }
      throw error;
    }
  },

  /**
   * Delete a document
   * @param {string} id - Document ID
   * @returns {Promise<Object>} - Response with success status
   */
  async deleteDocument(id) {
    try {
      const response = await api.delete(`/documents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  /**
   * Process a document to create RAG
   * @param {string} id - Document ID
   * @returns {Promise<Object>} - Response with processing status
   */
  async processDocument(id) {
    try {
      console.log(`Sending request to process document ID: ${id}`);
      // Use axios directly with explicit credentials to ensure authentication is sent
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/documents/${id}/process`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        timeout: 60000 // 60 second timeout for processing
      });
      console.log(`Document processing response:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Error processing document:', error);
      // Provide more detailed error information
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      } else if (error.request) {
        console.error('No response received from server');
      } else {
        console.error('Error setting up request:', error.message);
      }
      throw error;
    }
  },

  /**
   * Get document details
   * @param {string} id - Document ID
   * @returns {Promise<Object>} - Response with document details
   */
  async getDocumentDetails(id) {
    try {
      const response = await api.get(`/documents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching document details:', error);
      throw error;
    }
  }
};
